<script setup lang="ts">
import { useAppIndex } from '@base/composables/appIndex'
import { ECarts, EScreenSize, ESpecialPage } from '@base/utils/constants'
import { ut_wilo_isInternal, ut_wilo_isWiloWorldUser, ut_wilo_realCountry } from '~/utils/utils'
import {
  ut_getCartType,
  ut_isEmbedded,
  ut_isLoggedIn,
  ut_isPrint,
  ut_isScreenSize,
  ut_navigateNewRoute,
} from '@base/utils/utils'
import { type ExtCmd, ExtCmdType, PARAM } from '@amirada/lib-wilo-shared'
import { usePfinderAPI } from '~/composables/pfinderAPI'
import type { IParamQuery } from '@base/utils/types'

const cp = useAppIndex()
const runtimeConfig = useRuntimeConfig()
const pfinderAPI = usePfinderAPI()

if (runtimeConfig.public.loadCss) {
  useHead({
    link: [
      { href: 'https://wilo.com/resources/v121/css/style.css', rel: 'stylesheet' },
    ],
  })
}

onMounted(async () => {
  // single Tab by ArticleNo or SeriesID, redirect to real URL
  if (cp.indexStore.state.paramData[PARAM.isIFrame]) {
    const paramAr: string[] = cp.indexStore.state.paramData[PARAM.isIFrame].split('|')
    if (paramAr[0] && paramAr[1]) {
      const searchParam: string[] = paramAr[0].split(':')
      const key = searchParam[0] as 'id_product' | 'id_series' | 'uuid' | 'oid '
      const args: ExtCmd<ExtCmdType.EXP_SEARCH>['arg'] = { [key]: searchParam[1] }
      const res = await pfinderAPI.fetchData(ExtCmdType.EXP_SEARCH, args, {})
      if (res && res.result && res.result[0] && res.result[0].url) {
        const query: IParamQuery = {
          [PARAM.isIFrame]: cp.indexStore.state.paramData[PARAM.isIFrame],
          embedded: '1',
        }
        const pathAr: string[] = res.result[0].url.split('/')
        if (pathAr) {
          pathAr.shift() // remove lang from result
          const url: string = cp.indexStore.getRootPath + '/' + pathAr.join('/')
          ut_navigateNewRoute(url, query, '', false)
        }
      }
    }
  }
  isCartType.value = ut_getCartType(cp.indexStore.state.currentNode.nodeName as ESpecialPage)
  if (import.meta.client) {
    addScreenSizeListener()
    listenMediaQuery()
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    removeScreenSizeListener()
  }
})

const mediaQueryList: MediaQueryList[] = []

const mediaQueries: Partial<Record<EScreenSize, any>> = {
  [EScreenSize.sc_sm]: '(min-width: 640px)',
  [EScreenSize.sc_md]: '(min-width: 768px)',
  [EScreenSize.sc_lg]: '(min-width: 1024px)',
  /* [EScreenSize.sc_xl]: '(min-width: 1280px)',
  [EScreenSize.sc_2xl]: '(min-width: 1640px)', not necessary for project */
}

function addScreenSizeListener() {
  let index: number = 0
  for (const mediaQuery of Object.values(mediaQueries)) {
    mediaQueryList.push(window.matchMedia(mediaQuery))
    if (mediaQueryList[index]) {
      mediaQueryList[index]?.addEventListener('change', listenMediaQuery)
    }
    index++
  }
}

function listenMediaQuery() {
  let index: number = 0
  let size: EScreenSize = EScreenSize.sc_xs
  for (const mediaQuery in mediaQueries) {
    if (mediaQueryList[index]?.matches) {
      size = mediaQuery as EScreenSize
    }
    index++
  }
  cp.indexStore.setScreenSize(size)
}

function removeScreenSizeListener() {
  for (const mediaQuery of mediaQueryList) {
    mediaQuery.removeEventListener('change', () => {})
  }
}

const isHistoric = computed((): boolean => {
  return cp.indexStore.state.paramData.rcco.slice(-2) === '_h'
})

const isMotors = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c8fb7d8dc018fb867e6d058a0')
})

const isProducts = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c8e9774cf018ea36fa2c86c6b')
})

const isAccessories = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c8e9774cf018ea370051d1130')
})

const isApplication = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c8e9774cf018ea36fd8117179')
})

// A level2 node of the application tree: root13/<application>/<L0>/<L1>/<L2> — the depth is fixed.
const isApplicationLevel2 = computed((): boolean => {
  return isApplication.value && cp.indexStore.state.paramData.namePath.split('/').length === 5
})

const isInvisibleOem = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c88f57fa20189079e03ca4522')
})

const isServices = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c8e9774cf018ea37790a356d0')
})

const isLcc = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c8fb7d8dc018fb868b67b603e')
})

const isDpp = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('89e1809c9fe97b3e019ff0c74aaf2a4b')
})

const isInternStart = computed((): boolean => {
  return cp.indexStore.state.currentNode.nodeName === 'root13'
})

const isQuickSelect = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c8fb7d8dc018fb86872e95d8f')
})

// A node that brings its own view instead of the start-page navigation. Anything rendered below on
// its own `is…` flag belongs in here too — otherwise it draws ON TOP of the navigation, and nothing
// complains. One list instead of a negation repeated per page.
const hasOwnView = computed((): boolean => isQuickSelect.value || isApplication.value || isLcc.value || isDpp.value)

const isInternal = computed((): boolean => ut_wilo_isInternal())

const isSeries = computed((): boolean => {
  let res: boolean = false
  if (cp.indexStore.state.currentNode?.attrs) {
    res = cp.indexStore.state.currentNode.attrs['SERIES'] !== undefined
  }
  return res
})

const showKoreanTeaser = computed((): boolean => {
  return cp.indexStore.state.paramData.namePath.includes('8ae2819c7129647401712abc9a153e68') && cp.indexStore.state.paramData.rcco === 'kr'
})

const realCountry = computed((): string => {
  return ut_wilo_realCountry(isHistoric.value)
})

const countryLanguage = computed((): string => {
  return (cp.indexStore.state.paramData.embeddedInfo.embparam1 ? cp.indexStore.state.paramData.embeddedInfo.embparam1 : cp.indexStore.state.paramData.rcco)
    + '/' + (cp.indexStore.state.paramData.embeddedInfo.embparam3 ? cp.indexStore.state.paramData.embeddedInfo.embparam3 : cp.indexStore.state.paramData.lcda)
})

const isCartType: Ref<Partial<ECarts> | undefined> = ref(undefined)
</script>

<template>
  <!-- ignoreNonKeyboardFocus: a tooltip must not open on programmatic focus. Closing a modal
       restores focus to whatever was focused before it opened (reka-ui FocusScope) — usually the
       button that opened it, which is often a tooltip trigger. The tooltip would open on that
       focus and never close again, because the mouse is elsewhere and no pointerleave follows.
       With the flag it only opens on :focus-visible, so keyboard users still get it. -->
  <UApp :locale="cp.locale" portal="#__nuxt" :tooltip="{ ignoreNonKeyboardFocus: true }">
    <!-- Loading overlay div.
         z-5015 puts it into the band documented in app.config.ts (modal overlay 5000, modal
         content 5001, select popper 5010, tooltip 5020 — new overlays go BELOW the tooltip).
         ⚑ It used to be 5000 and therefore sat ONE below the modal content: while a modal
         collected its data the spinner was in the dom and invisible, measured 02.09.2026 with
         elementFromPoint at the viewport centre. "The app is busy" has to cover what it blocks —
         and it blocks the modal too. -->
    <div v-if="cp.indexStore.state.isLoading" class="h-full w-full left-0 top-0 fixed z-5015">
      <UIcon name="i-wilo-wilo-loading-general" size="100px" class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-4000 animate-fade-in" />
    </div>

    <!-- Login -->
    <UContainer v-if="!ut_isEmbedded() && !isInternal && !cp.indexStore.state.paramData[PARAM.isIFrame] && !useRuntimeConfig().public.noLogin">
      <ClientOnly>
        <LazyLoginAccess
          v-if="!cp.grantedAccess.value"
          :username="useRuntimeConfig().public.accessUser"
          :password="useRuntimeConfig().public.accessPassword"
        />
      </ClientOnly>
    </UContainer>

    <!-- Internal header -->
    <div v-if="isInternal" class="mb-4 sticky top-0 z-5000">
      <LazyMsal :is-intern-start="isInternStart" :is-historic="isHistoric" :real-country="realCountry" />
    </div>

    <!-- Content if access granted -->
    <template v-if="(cp.grantedAccess.value && !isInternal) || (isInternal && ut_isLoggedIn()) || cp.indexStore.state.paramData[PARAM.isIFrame] || useRuntimeConfig().public.noLogin">

      <template v-if="!ut_isPrint()">

        <!-- Wilo World import parameter (path + psx), for the users on def_wiloWorldlist -->
        <LazyWiloWorldPath v-if="isInternal && isApplicationLevel2 && ut_wilo_isWiloWorldUser()" />

        <!-- Settings and search -->
        <template v-if="!ut_isEmbedded() && !isInternal && !cp.indexStore.state.paramData[PARAM.isIFrame] && !useRuntimeConfig().public.noSettings">
          <UContainer class="my-2">
            <LazySettings :is-historic="isHistoric" :real-country="realCountry" />
            <LazySearchInput :is-historic="isHistoric" :use-adesso="false" :real-country="realCountry" :is-settings="true" />

            <!-- Raw URL -->
            <ClientOnly v-if="runtimeConfig.public.showRawURL">
              <div class="my-4">
                <b v-html="'Raw URL:'" />
                <div v-html="cp.rawurl.value" />
              </div>
            </ClientOnly>
          </UContainer>
          <hr class="my-4">
        </template>

        <!-- Carts mobile Breadcrumb, Settings small -->
        <UContainer v-if="!ut_isEmbedded() && !cp.indexStore.state.paramData[PARAM.isIFrame]">
          <!-- Carts mobile -->
          <LazyCartIcons v-if="!ut_isEmbedded() && ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])" :is-side-bar="true" :real-country="realCountry" class="lg:hidden mb-2" />

          <div class="md:flex mb-12">
            <!-- Breadcrumb -->
            <LazyBreadCrumb v-if="!ut_isEmbedded()" :is-historic="isHistoric" :real-country="realCountry" />

            <!-- Settings small -->
            <div v-if="isInternal && isInternStart" class="md:ml-auto mt-2 md:mt-0">
              <div class="flex flex-wrap gap-x-4 gap-y-2">
                <SettingsSmall :is-historic="isHistoric" :real-country="realCountry" :is-internal="isInternal" :show-frequency="cp.indexStore.getIsNavigation || (cp.indexStore.getIsConfig && !isSeries)" />
              </div>
            </div>
          </div>
        </UContainer>

        <!-- Carts desktop -->
        <UCollapsible
          v-if="!ut_isEmbedded() && !cp.indexStore.state.paramData[PARAM.isIFrame] && !ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])"
          class="hidden lg:block"
          :default-open="true"
          :ui="{
            content: 'data-[state=open]:animate-none data-[state=closed]:animate-none',
          }"
        >
          <UButton
            icon="i-wilo-chevron-right-solid-full"
            color="secondary"
            class="group fixed top-1/2 -translate-y-1/2 right-15 data-[state=closed]:right-0 z-3000"
            aria-label="Show carts"
            :ui="{
              base: 'rounded-none',
              leadingIcon: 'group-data-[state=closed]:rotate-180 transition-transform duration-200',
            }"
          />
          <template #content>
            <LazyCartIcons :is-side-bar="true" :real-country="realCountry" class="hidden lg:block fixed top-1/2 transform -translate-y-1/2 right-0 z-3000" />
          </template>
        </UCollapsible>

        <!-- Navigation (start page) -->
        <LazyNavigation
          v-if="cp.indexStore.getIsNavigation && !hasOwnView && !cp.indexStore.state.paramData[PARAM.isIFrame]"
          :is-application="false"
          :is-internal="isInternal"
          :real-country="realCountry"
        />

      </template>

      <!-- Configurations which are not series or Application -->
      <template v-if="(cp.indexStore.getIsConfig && !isSeries) || (isApplication && cp.indexStore.getIsNavigation)">
        <LazyOverview
          :country-language="countryLanguage"
          :real-country="realCountry"
          :is-historic="isHistoric"
          :is-navigation="cp.indexStore.getIsNavigation"
          :is-config="cp.indexStore.getIsConfig"
          :is-application="isApplication"
          :is-products="isProducts"
          :is-accessories="isAccessories"
          :is-internal="isInternal"
          :is-series="isSeries"
          :is-motors="isMotors"
          :show-korean-teaser="showKoreanTeaser"
        />
      </template>

      <!-- Detailpage (Series or Product) -->
      <LazyDetail
        v-if="cp.indexStore.getIsResultNode || isSeries"
        :is-historic="isHistoric"
        :is-motors="isMotors"
        :is-invisible-oem="isInvisibleOem"
        :is-services="isServices"
        :is-products="isProducts"
        :is-series="isSeries"
        :is-internal="isInternal"
        :real-country="realCountry"
        :country-language="countryLanguage"
      />

      <!-- Special pages -->
      <template v-if="cp.indexStore.getIsSpecialPage">
        <LazySearch v-if="cp.indexStore.state.currentNode.nodeName === ESpecialPage.sp_search_result" />
        <LazyExternalData v-else-if="cp.indexStore.state.currentNode.nodeName === ESpecialPage.sp_custom" />
        <LazyCart
          v-else-if="isCartType"
          :cart="isCartType"
          :is-internal="isInternal"
          :real-country="realCountry"
          :country-language="countryLanguage"
        />
      </template>

      <!-- Digital product pass -->
      <LazyDpp
        v-if="isDpp && !ut_isEmbedded()"
        :is-historic="isHistoric"
        :is-motors="isMotors"
        :is-invisible-oem="isInvisibleOem"
        :is-services="isServices"
        :is-products="isProducts"
        :is-internal="isInternal"
        :country-language="countryLanguage"
        :real-country="realCountry"
      />

      <!-- LCC -->
      <LazyCart
        v-if="isLcc"
        :cart="ECarts.ct_custom_1"
        :is-internal="isInternal"
        :real-country="realCountry"
        :country-language="countryLanguage"
      />

      <!-- Quickselect -->
      <LazyQuickSelect
        v-if="isQuickSelect"
        :is-internal="isInternal"
        :real-country="realCountry"
        :country-language="countryLanguage"
        :show-modal-pdf="false"
      />

    </template>

    <!-- Footer to simulate normal document height -->
    <div v-if="!ut_isEmbedded() && !ut_isPrint()" class="h-60" />

    <!-- Not in a print run: nobody reads a toast in a pdf, and being lazy it is not even fetched
         there. ⚑ Do NOT take this as "in print we are the last element again" — the trailing spacer
         is decided in Detail.vue (lastPrintedTab), deliberately not by a css :last-child, and it
         must stay that way whatever stands behind us. -->
    <template v-if="!ut_isEmbedded() && !ut_isPrint()">
      <LazyNotifications />
    </template>
  </UApp>
</template>
